<script setup>
import { useI18n } from 'vue-i18n'
import AppButton from './ui/AppButton.vue'
import AppImage from './ui/AppImage.vue'
import RevealOnScroll from './ui/RevealOnScroll.vue'
import { media } from '@/data/content'
import { useSection } from '@/i18n'
import { useNetworkCanvas } from '@/composables/useNetworkCanvas'
import { useDemoModal } from '@/composables/useDemoModal'

const { t } = useI18n()
const s = useSection('hero')
const { canvas } = useNetworkCanvas()
const { open: openDemo } = useDemoModal()
</script>

<template>
  <section id="top" class="relative grid min-h-[100svh] place-items-center px-6 pb-[90px] pt-[140px]">
    <canvas ref="canvas" aria-hidden="true" class="absolute inset-0 block h-full w-full" />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(15,138,99,.10),rgba(251,252,251,0)_60%)]"
    />

    <div class="relative w-full max-w-hero text-center">
      <RevealOnScroll
        class="inline-flex items-center gap-[10px] rounded-full border border-hairline bg-white/80 px-[14px] py-[7px] text-[12.5px] font-semibold uppercase tracking-[0.1em] text-muted"
      >
        <span aria-hidden="true" class="h-1.5 w-1.5 rounded-full bg-brand animate-om-pulse" />
        {{ s.badge }}
      </RevealOnScroll>

      <RevealOnScroll
        as="h1"
        :delay="80"
        class="mt-[26px] font-display text-[clamp(38px,6.2vw,72px)] font-semibold leading-[1.04] tracking-[-0.03em] [text-wrap:balance]"
      >
        {{ s.headline[0] }}<br />{{ s.headline[1] }}
      </RevealOnScroll>

      <RevealOnScroll
        as="p"
        :delay="160"
        class="mx-auto mt-[22px] max-w-[660px] text-[clamp(15px,1.5vw,18px)] leading-[1.65] text-muted [text-wrap:pretty]"
      >
        {{ s.lede }}
      </RevealOnScroll>

      <RevealOnScroll :delay="240" class="mt-[34px] flex flex-wrap justify-center gap-3">
        <AppButton href="#ecosystem" variant="primary">{{ t('ui.actions.exploreOmase') }}</AppButton>
        <AppButton variant="outline" @click="openDemo">{{ t('ui.actions.requestDemo') }}</AppButton>
      </RevealOnScroll>

      <RevealOnScroll
        as="ul"
        :delay="400"
        class="mt-14 flex list-none flex-wrap justify-center gap-x-[26px] gap-y-[10px] font-display text-xs uppercase tracking-[0.16em] text-dim"
      >
        <li v-for="step in s.chain" :key="step">{{ step }}</li>
      </RevealOnScroll>

      <RevealOnScroll as="figure" :delay="350" class="mt-[52px] text-left">
        <div
          class="overflow-hidden rounded-[18px] border border-hairline bg-night shadow-[0_50px_100px_-60px_rgba(16,22,28,.65)]"
        >
          <!-- LCP image: eager, high priority, intrinsic size reserved. -->
          <AppImage v-bind="media.hero" :alt="s.imageAlt" loading="eager" class="block h-auto w-full" />
        </div>
        <figcaption
          class="mt-[18px] flex flex-wrap items-center justify-center gap-x-[18px] gap-y-2 font-display text-xs uppercase tracking-[0.14em] text-dim"
        >
          <span>{{ s.captions[0] }}</span>
          <span class="text-brand-deep">{{ s.captions[1] }}</span>
        </figcaption>
      </RevealOnScroll>
    </div>
  </section>
</template>
